import { type ReactNode } from "react";
import { DisclosureChevron, ExpandIn } from "@/components/common/Disclosure";
import { type ToolCallStatus } from "@/lib/chat";
import type { ToolUseRenderBlock } from "@/lib/chat-render";
import { UserChoiceCard } from "./UserChoiceCard";
import { SubagentGroupCard } from "./SubagentGroupCard";
import { WorkflowCard } from "./WorkflowCard";
import { HEADER_BUTTON_CLASS, StatusDot, ToolDetail, ToolEntry, ToolHeaderRow } from "./ToolEntry";
import { toolCardKind } from "./tool-card-kind";
import { hasToolDetails, toolEntryText } from "./tool-entry-label";
import { ToolEntryLabel } from "./ToolEntryLabel";
import { buildViewEntries, summaryText } from "./tool-group-summary";
import { useDisclosureOpen } from "./useDisclosureOpen";
import { useChatStore } from "@/stores/useChatStore";

interface ToolGroupCardProps {
	tools: ToolUseRenderBlock[];
}

function getGroupStatus(tools: ToolUseRenderBlock[]): ToolCallStatus {
	if (tools.some((t) => t.status === "running")) return "running";
	if (tools.some((t) => t.status === "waiting_user")) return "waiting_user";
	if (tools.some((t) => t.status === "error")) return "error";
	if (tools.some((t) => t.status === "cancelled")) return "cancelled";
	if (tools.some((t) => t.status === "skipped")) return "skipped";
	return "completed";
}

function CardHeader({
	label,
	status,
	expanded,
	hasToggle,
	onToggle,
}: {
	/** 省略され得る 1 行の要素。全文の tooltip は呼び出し側で付ける。 */
	label: ReactNode;
	status: ToolCallStatus;
	expanded: boolean;
	hasToggle: boolean;
	onToggle: () => void;
}) {
	return (
		<button
			type="button"
			disabled={!hasToggle}
			onClick={onToggle}
			className={`min-w-0 ${HEADER_BUTTON_CLASS}`}
		>
			<StatusDot status={status} />
			{label}
			{hasToggle && <DisclosureChevron open={expanded} size={12} />}
		</button>
	);
}

export function ToolGroupCard({ tools }: ToolGroupCardProps) {
	const hitlTools = tools.filter((tool) => toolCardKind(tool) === "hitl");
	const subagentTools = tools.filter((tool) => toolCardKind(tool) === "subagent");
	const workflowTools = tools.filter((tool) => toolCardKind(tool) === "workflow");
	const otherTools = tools.filter((tool) => toolCardKind(tool) === "generic");
	return (
		<div className="flex flex-col gap-2">
			{hitlTools.length > 0 && <UserChoiceCard tools={hitlTools} />}
			{subagentTools.length > 0 && <SubagentGroupCard tools={subagentTools} />}
			{workflowTools.length > 0 && <WorkflowCard tools={workflowTools} />}
			{otherTools.length > 0 && <ToolGroupCardInner tools={otherTools} />}
		</div>
	);
}

function ToolGroupCardInner({ tools }: ToolGroupCardProps) {
	const disclosureOpen = useChatStore((s) => s.disclosureOpen);
	const setDisclosureOpen = useChatStore((s) => s.setDisclosureOpen);
	const [listExpandedByUser, setListExpandedByUser] = useDisclosureOpen(
		`tool-list:${tools[0].key}`,
	);
	const toolExpandedByUser = (tool: ToolUseRenderBlock): boolean | undefined =>
		disclosureOpen[`tool:${tool.key}`];
	const toggleTool = (tool: ToolUseRenderBlock, expanded: boolean) =>
		setDisclosureOpen(`tool:${tool.key}`, !expanded);
	const runningTools = tools.filter((tool) => tool.status === "running");
	const isRunning = runningTools.length > 0;
	const status = getGroupStatus(tools);
	const sortedEntries = [...buildViewEntries(tools)].sort(
		(a, b) => Number(a.tool.status === "running") - Number(b.tool.status === "running"),
	);
	if (sortedEntries.length === 1) {
		const tool = sortedEntries[0].tool;
		const expanded = toolExpandedByUser(tool) ?? false;
		return (
			<div
				data-testid={isRunning ? "tool-call-running" : "tool-call"}
				data-status={status}
				className="min-w-0"
			>
				<ToolHeaderRow tools={[tool]}>
					<CardHeader
						label={<ToolEntryLabel tool={tool} />}
						status={status}
						expanded={expanded}
						hasToggle={hasToolDetails(tool)}
						onToggle={() => toggleTool(tool, expanded)}
					/>
				</ToolHeaderRow>
				{expanded && (
					<ExpandIn>
						<ToolDetail tool={tool} />
					</ExpandIn>
				)}
			</div>
		);
	}
	const listExpanded =
		listExpandedByUser ?? tools.some((tool) => toolExpandedByUser(tool) === true);
	// 実行中のツールにはまだ差分が無いので、ラベルは文字列だけで足りる。
	const label = !isRunning
		? summaryText(sortedEntries)
		: runningTools.length === 1
			? toolEntryText(runningTools[0])
			: `${runningTools.length}件のツールを実行中…`;
	return (
		<div
			data-testid={isRunning ? "tool-call-running" : "tool-call"}
			data-status={status}
			className="min-w-0"
		>
			<ToolHeaderRow tools={sortedEntries.map((entry) => entry.tool)}>
				<CardHeader
					label={
						// 実行中は見出しの文言が入れ替わるので、title ではなく data-tooltip で出す。
						<span data-tooltip={label} data-tooltip-if-truncated className="min-w-0 truncate">
							{label}
						</span>
					}
					status={status}
					expanded={listExpanded}
					hasToggle
					onToggle={() => setListExpandedByUser(!listExpanded)}
				/>
			</ToolHeaderRow>
			{listExpanded && (
				<ExpandIn className="mt-1">
					{sortedEntries.map((entry, index) => {
						const expandedByUser = toolExpandedByUser(entry.tool);
						const expanded = expandedByUser ?? entry.tool.status === "running";
						return (
							<div key={entry.key}>
								<ToolEntry
									tool={entry.tool}
									expanded={expanded}
									animateExpand={expandedByUser !== undefined}
									onToggle={() => toggleTool(entry.tool, expanded)}
								/>
								{index < sortedEntries.length - 1 && (
									<div className="border-b border-line-1 my-0.5" />
								)}
							</div>
						);
					})}
				</ExpandIn>
			)}
		</div>
	);
}
